import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { UploadCloud, FileSpreadsheet, AlertTriangle, ArrowLeft, Trash2, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const BRANCH_CODES = ["PBY", "CHP", "VZK", "BCS", "BPC", "BNE"];

/**
 * Bulk Patient Import
 * Accepts .csv / .xlsx with columns:
 * NAME, UHID NO, HOSPITAL BRANCH, PHONE, ADDRESS, COMPLAINTS OF, CONSULTED DOCTOR
 * Header naming is flexible (normalised to lowercase, non-alnum stripped).
 */
export default function BulkPatientImport() {
  const { branch } = useAuth();
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const norm = (k) => String(k || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const pick = (row, ...keys) => {
    for (const key of keys) {
      for (const rk of Object.keys(row)) {
        if (norm(rk) === norm(key)) return row[rk];
      }
    }
    return "";
  };

  const parseFile = async (file) => {
    setFileName(file.name);
    setResult(null);
    let raw = [];
    const ext = file.name.toLowerCase().split(".").pop();
    if (ext === "xlsx" || ext === "xls") {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      raw = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
    } else {
      const text = await file.text();
      const [head, ...body] = text.split(/\r?\n/).filter((l) => l.trim());
      const headers = head.split(",").map((s) => s.replace(/^"|"$/g, "").trim());
      raw = body.map((line) => {
        const cells = line.split(",").map((s) => s.replace(/^"|"$/g, "").trim());
        return headers.reduce((o, h, i) => ({ ...o, [h]: cells[i] || "" }), {});
      });
    }

    const normalised = raw.map((r, i) => ({
      _row: i + 2,
      uhid: String(pick(r, "uhid no", "uhid", "mrn", "uhidno")).trim(),
      name: String(pick(r, "name", "patient name", "patient")).trim(),
      branch_code: String(pick(r, "hospital branch", "branch", "branch code", "branch_code")).trim().toUpperCase(),
      phone: String(pick(r, "phone", "mobile", "contact")).trim(),
      address: String(pick(r, "address")).trim(),
      complaint: String(pick(r, "complaints of", "complaint", "chief complaint")).trim(),
      consulted_doctor: String(pick(r, "consulted doctor", "doctor", "physician")).trim(),
      age: Number(String(pick(r, "age")).replace(/[^0-9]/g, "")) || 0,
      gender: String(pick(r, "gender", "sex")).trim().toLowerCase() || "unknown",
    }));
    setRows(normalised);
    toast.success(`Parsed ${normalised.length} rows from ${file.name}`);
  };

  const invalid = useMemo(() => rows.filter((r) => !r.name || !BRANCH_CODES.includes(r.branch_code)), [rows]);

  const submit = async () => {
    if (rows.length === 0) return;
    if (invalid.length > 0) {
      const proceed = window.confirm(
        `${invalid.length} row${invalid.length === 1 ? "" : "s"} look invalid (missing name or unknown branch). Send everything to the server anyway — invalid rows will be listed with a reason in the result panel.`
      );
      if (!proceed) return;
    }
    setBusy(true);
    try {
      // Send ALL parsed rows to the server so the result panel accurately
      // reports every rejection reason (invalid branch, missing name,
      // duplicate UHID, doctor not at branch, etc.) instead of silently
      // dropping rows client-side.
      const payload = rows.map(({ _row, ...rest }) => rest);
      const { data } = await api.post("/patients/bulk", { rows: payload });
      setResult(data);
      toast.success(`Imported ${data.ok} · Failed ${data.failed}`);
    } catch (err) {
      toast.error(formatApiError(err, "Import failed"));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setRows([]);
    setFileName("");
    setResult(null);
  };

  return (
    <div className="max-w-5xl mx-auto animate-fade-up" data-testid="bulk-import-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
            Bulk patient import · {branch?.name}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-forest mb-1">Upload patient list</h1>
          <p className="text-[#5C5652]">
            Accepts CSV or Excel with columns: <span className="font-mono text-xs">NAME · UHID NO · HOSPITAL BRANCH · PHONE · ADDRESS · COMPLAINTS OF · CONSULTED DOCTOR</span>. UHID is preserved so your paper records line up.
          </p>
        </div>
        <Link to="/patients" data-testid="bulk-back" className="h-10 px-4 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] text-sm font-semibold inline-flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} /> Patient search
        </Link>
      </div>

      <div className="bg-white border-2 border-dashed border-sand rounded-lg p-8 text-center mb-6">
        <UploadCloud className="h-10 w-10 mx-auto text-forest mb-3" strokeWidth={1.5} />
        <label className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover cursor-pointer">
          <FileSpreadsheet className="h-4 w-4" strokeWidth={1.75} />
          Choose CSV / Excel
          <input
            data-testid="bulk-file-input"
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && parseFile(e.target.files[0])}
          />
        </label>
        {fileName && (
          <div className="mt-3 text-xs text-[#5C5652] flex items-center justify-center gap-2">
            <span className="font-mono">{fileName}</span> · {rows.length} rows parsed
            <button onClick={reset} data-testid="bulk-reset" className="text-forest hover:underline inline-flex items-center gap-1">
              <RefreshCw className="h-3 w-3" strokeWidth={2} /> Reset
            </button>
          </div>
        )}
      </div>

      {rows.length > 0 && !result && (
        <div className="bg-white border border-sand rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-sand flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-wider text-[#5C5652]">Preview ({rows.length} rows)</div>
            {invalid.length > 0 && (
              <div className="inline-flex items-center gap-1.5 text-xs text-[#C25946] font-semibold">
                <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2} /> {invalid.length} invalid row{invalid.length === 1 ? "" : "s"}
              </div>
            )}
          </div>
          <div className="max-h-[420px] overflow-auto">
            <table className="w-full text-sm" data-testid="bulk-preview-table">
              <thead className="bg-[#F9F6F0] text-[10px] uppercase tracking-wider text-[#5C5652] sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-bold">#</th>
                  <th className="text-left px-3 py-2 font-bold">UHID</th>
                  <th className="text-left px-3 py-2 font-bold">Name</th>
                  <th className="text-left px-3 py-2 font-bold">Branch</th>
                  <th className="text-left px-3 py-2 font-bold">Phone</th>
                  <th className="text-left px-3 py-2 font-bold">Complaint</th>
                  <th className="text-left px-3 py-2 font-bold">Doctor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E3D9]">
                {rows.slice(0, 200).map((r, i) => {
                  const bad = !r.name || !BRANCH_CODES.includes(r.branch_code);
                  return (
                    <tr key={i} data-testid={`bulk-row-${i}`} className={bad ? "bg-[#C25946]/[0.05]" : ""}>
                      <td className="px-3 py-2 font-mono text-[10px] text-[#5C5652]">{r._row}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.uhid || "(auto)"}</td>
                      <td className="px-3 py-2 font-semibold">{r.name || <span className="text-[#C25946]">— missing —</span>}</td>
                      <td className="px-3 py-2 font-mono">{BRANCH_CODES.includes(r.branch_code) ? r.branch_code : <span className="text-[#C25946]">{r.branch_code || "—"}</span>}</td>
                      <td className="px-3 py-2 text-xs">{r.phone}</td>
                      <td className="px-3 py-2 text-xs truncate max-w-[220px]" title={r.complaint}>{r.complaint}</td>
                      <td className="px-3 py-2 text-xs">{r.consulted_doctor}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.length > 200 && (
              <div className="text-center text-xs text-[#5C5652] py-2 border-t border-sand">+ {rows.length - 200} more rows will import (not shown here)</div>
            )}
          </div>
          <div className="p-4 border-t border-sand flex gap-3">
            <button onClick={submit} disabled={busy} data-testid="bulk-submit" className="flex-1 h-11 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover disabled:opacity-60 inline-flex items-center justify-center gap-2">
              <UploadCloud className="h-4 w-4" strokeWidth={1.75} />
              {busy ? "Importing…" : `Send ${rows.length} row${rows.length === 1 ? "" : "s"} to server`}
            </button>
            <button onClick={reset} disabled={busy} className="h-11 px-4 rounded-lg border border-sand hover:bg-[#F9F6F0] inline-flex items-center gap-2">
              <Trash2 className="h-4 w-4" strokeWidth={1.5} /> Cancel
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-white border border-sand rounded-lg p-6" data-testid="bulk-result">
          <div className="text-xs font-bold uppercase tracking-wider text-[#5C5652] mb-3 ornament-dot">Import complete</div>
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="p-4 rounded-lg bg-[#1A4331]/[0.06] border border-sand text-center">
              <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Added</div>
              <div className="font-display text-3xl text-forest" data-testid="bulk-ok">{result.ok}</div>
            </div>
            <div className="p-4 rounded-lg bg-[#C25946]/10 border border-sand text-center">
              <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Failed</div>
              <div className="font-display text-3xl text-[#C25946]" data-testid="bulk-failed">{result.failed}</div>
            </div>
            <div className="p-4 rounded-lg bg-[#F9F6F0] border border-sand text-center">
              <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Total rows</div>
              <div className="font-display text-3xl text-[#5C5652]">{result.total}</div>
            </div>
          </div>
          {result.failures?.length > 0 && (
            <>
              <div className="text-xs font-bold uppercase tracking-wider text-[#5C5652] mb-2">First {result.failures.length} failure reason{result.failures.length === 1 ? "" : "s"}</div>
              <ul className="space-y-1.5 text-xs">
                {result.failures.map((f, i) => (
                  <li key={i} className="p-2 rounded border border-[#C25946]/25 bg-[#C25946]/[0.06]">
                    <div className="font-mono text-[10px] text-[#5C5652]">Row {f.row}{f.uhid ? ` · UHID ${f.uhid}` : ""}</div>
                    <div className="font-semibold">{f.name}</div>
                    <div className="text-[#C25946] mt-0.5">{f.reason}</div>
                  </li>
                ))}
              </ul>
            </>
          )}
          <button onClick={reset} data-testid="bulk-again" className="mt-5 h-11 px-5 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover">
            Import another file
          </button>
        </div>
      )}
    </div>
  );
}
